import React, { useState, useEffect, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import InfoTooltipSvg from '@/components/common/InfoTooltipSvg';
import LoadingSvg from '@/components/common/LoadingSvg';
import { Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverClose } from '@/components/common/popover';
import { createPaypalTransaction } from '@/external/service/paypal.service';
import { Cookie } from '@/tscapi/utils/cookie';

import { Dialog } from './Dialog';
import DonationSelector from './DonationSelector';
import { Easypay } from './EasyPay';
import { EasyPayModify } from './EasyPayModify';
import { useAuthenticated } from './hooks/useAuthenticated';
import { useCart, useRemoveDonation } from './hooks/useCart';
import { useCartData } from './hooks/useCartData';
import { useEstimatedTax } from './hooks/useEstimatedTax';
import { useOrder } from './hooks/useOrder';
import { useOrderReview } from './hooks/useOrderReview';
import { useTaxProvince } from './hooks/useTaxes';
import { en, fr } from './i18n';
import { Promocode } from './Promocode';
import { setupAnalyticsCancelModification } from './utils/analyticsEvents';
import { getShowBagEmpty, cartContainsOnlyDonation } from './utils/isBagEmpty';

const province = [
    { name: 'Alberta', id: 'AB' },
    { name: 'British Columbia', id: 'BC' },
    { name: 'Manitoba', id: 'MB' },
    { name: 'New Brunswick', id: 'NB' },
    { name: 'Newfoundland and Labrador', id: 'NL' },
    { name: 'Northwest Territories', id: 'NT' },
    { name: 'Nova Scotia', id: 'NS' },
    { name: 'Nunavut', id: 'NU' },
    { name: 'Ontario', id: 'ON' },
    { name: 'Prince Edward Island', id: 'PE' },
    { name: 'Quebec', id: 'QC' },
    { name: 'Saskatchewan', id: 'SK' },
    { name: 'Yukon Territory', id: 'YT' },
];

interface OrderSummaryProps {
    isModifyOrder: boolean;
    isCheckoutDisabled: boolean;
    isFraud: boolean;
    isPaypalDisabled: boolean;
    easyPayWarningBlockCheckout: boolean;
    showEasyPayNotice: boolean;
    setIsPaypalDisabled: React.Dispatch<React.SetStateAction<boolean>>;
    setIsCheckoutDisabled: React.Dispatch<React.SetStateAction<boolean>>;
    setEasyWarningBlockCheckout: React.Dispatch<React.SetStateAction<boolean>>;
    setShowEasyPayNotice: React.Dispatch<React.SetStateAction<boolean>>;
}
interface EstimatedTaxData {
    TaxTotal: string;
    GrandTotal: string;
}
declare let paypal: any;

const OrderSummary = ({
    isModifyOrder,
    isCheckoutDisabled,
    isFraud,
    isPaypalDisabled,
    setIsPaypalDisabled,
    setIsCheckoutDisabled,
    easyPayWarningBlockCheckout,
    setEasyWarningBlockCheckout,
    setShowEasyPayNotice,
    showEasyPayNotice,
}: OrderSummaryProps) => {
    //State
    const paypalRef = useRef();
    const cookie: Cookie = new Cookie();
    const isDonationFlowEnabled = window['donationFlowEnabled'];
    const [estTaxData, setEstTaxData] = useState<EstimatedTaxData>(null);
    const [isPaypalLoaded, setIsPaypalLoaded] = useState(false);
    const [paypalPaymentApproved, setPaypalPaymentApproved] = useState(false);
    const [hidePaypalButton, setHidePaypalButton] = useState(false);
    const [isPaypalButtonRendered, setIsPaypalButtonRendered] = useState(false);
    const [isModifyCheckoutDisabled, setIsModifyCheckoutDisabled] = useState(true);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    //Translation
    const { i18n, t } = useTranslation('shoppingbag');
    i18n.addResourceBundle('en', 'shoppingbag', en, true, true);
    i18n.addResourceBundle('fr', 'shoppingbag', fr, true, true);
    //React query data & hooks
    const isAuthenticated = useAuthenticated();
    const order = useOrder(isAuthenticated, cookie.ModifyOrderNumber);
    const { data } = useCart(isAuthenticated);
    const {
        data: orderReviewData,
        isFetching,
        isLoading,
    } = useOrderReview(isAuthenticated, cookie.ModifyOrderNumber, data);
    const { donationProduct, showSummaryDetails } = useCartData(data);
    const mutation = useRemoveDonation(donationProduct?.Id, cookie.CartGuid);
    const { taxProvince, setTaxProvince, defaultTax } = useTaxProvince();
    const estTaxMutation = useEstimatedTax(cookie.CartGuid);

    const onCheckout = () => {
        if (isFraud || isCheckoutDisabled) {
            return;
        }
        if (!easyPayWarningBlockCheckout) {
            window.location.href = '/pages/expresscheckout';
        } else {
            setShowEasyPayNotice(true);
        }
    };

    //Setup Paypal library
    useEffect(() => {
        if (
            data != null &&
            data?.OrderSummary?.TotalQuantity > 0 &&
            !isFraud &&
            !isPaypalDisabled &&
            !isCheckoutDisabled &&
            !isModifyOrder
        ) {
            if (!isPaypalLoaded) {
                const paypalClientId = window['payPalClientId'];
                const script = document.createElement('script');
                script.src =
                    'https://www.paypal.com/sdk/js?client-id=' +
                    paypalClientId +
                    '&currency=CAD' +
                    // + "&debug=true"
                    '&locale=en_CA&disable-funding=credit,card&commit=false&intent=order';
                script.setAttribute('data-namespace', 'paypal');
                script.addEventListener('load', () => setIsPaypalLoaded(true));
                document.body.appendChild(script);
            }
            if (isPaypalLoaded && !isPaypalButtonRendered) {
                const renderPayPal = async () => {
                    setIsPaypalButtonRendered(true);
                    paypal
                        .Buttons({
                            style: {
                                layout: 'vertical',
                                color: 'gold',
                                shape: 'rect',
                                label: 'checkout',
                            },
                            createOrder: async (ppData, actions) => {
                                //clear paypal info to reapprove new payment transaction
                                const cookie = new Cookie();
                                cookie.deletePaypalInfo();
                                const paymentResponse = await getPayPalPaymentResponse(data);
                                cookie.PaypalPaymentId = paymentResponse.id;
                                return paymentResponse.token;
                            },
                            onApprove: async (data, actions) => {
                                setPaypalPaymentApproved(true);
                                window.location.href = '/pages/expresscheckout?source=shoppingbag';
                            },
                            onCancel: () => {
                                const cookie = new Cookie();
                                cookie.deletePaypalInfo();
                            },
                            onError: (err) => {
                                console.error(err);
                            },
                        })
                        .render(paypalRef.current);
                };
                setTimeout(() => {
                    renderPayPal();
                });
            }
        }
    }, [isPaypalLoaded, data, isCheckoutDisabled, isFraud, isPaypalDisabled, isPaypalButtonRendered, isModifyOrder]);

    // if ordermod checkout button should be disabled
    useEffect(() => {
        if (isModifyOrder) {
            if (orderReviewData?.PreviousOrderTotal != orderReviewData?.GrandTotal) {
                setIsModifyCheckoutDisabled(false);
            } else {
                setIsModifyCheckoutDisabled(true);
            }
        }
    }, [orderReviewData?.GrandTotal, orderReviewData?.PreviousOrderTotal, setIsModifyCheckoutDisabled, isModifyOrder]);

    // show/hide paypal button
    useEffect(() => {
        if (isPaypalDisabled || data?.OrderSummary?.TotalQuantity == 0 || isCheckoutDisabled) {
            setHidePaypalButton(true);
        } else {
            setHidePaypalButton(false);
        }
    }, [isPaypalDisabled, data?.OrderSummary?.TotalQuantity, isCheckoutDisabled]);

    const getPayPalPaymentResponse = async (cartData) => {
        try {
            const result = await createPaypalTransaction(cartData);
            if (result && result.data && result.data.token) {
                return result.data;
            }
        } catch (error) {
            //setErrorMessage(error);
            console.log(error);
        }
    };

    // if (isModifyOrder && isLoading) {
    //     return (
    //         <div className="summary" style={{ background: '#fff' }}>
    //             <div className="summary__loading">
    //                 <LoadingSvg />
    //                 <div style={{ textAlign: 'center', padding: '15px 24px 0' }}>
    //                     {t(
    //                         "Please wait while we retrieve your order summary. If it's taking too long please refresh the page.",
    //                     )}
    //                     Load
    //                 </div>
    //             </div>
    //         </div>
    //     );
    // }

    if (isFetching) {
        return (
            <div className="summary" style={{ background: '#fff' }}>
                <div className="summary__loading">
                    <LoadingSvg />
                    <div style={{ textAlign: 'center', padding: '15px 24px 0' }}>
                        {t(
                            "Please wait while we retrieve your order summary. If it's taking too long please refresh the page.",
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (order.error) {
        return <>Error loading the Order Summary</>;
    }

    if (isModifyOrder && order.data == undefined) {
        return null;
    }

    const itemNumbers = ['339880', '339881', '339882', '339883', '339884'];

    return (
        <div className="summary" id="summary">
            <div
                className={`-mx-[21px] my-0 px-[25px] py-[30px] md3:m-0 ${
                    isDonationFlowEnabled
                        ? 'bg-gray-50'
                        : data?.OrderSummary?.TotalQuantity === 0
                          ? 'bg-white'
                          : 'bg-gray-50'
                }`}
                id="summary"
            >
                {isModifyOrder && (
                    <h2 className="mb-5 mt-[15px] text-[19px] font-semibold">
                        {t('Order summary')} ({order?.data?.OrderSummary.Quantity + data?.OrderSummary?.TotalQuantity}{' '}
                        {order?.data?.OrderSummary.Quantity + data?.OrderSummary?.TotalQuantity < 2
                            ? t('item')
                            : t('items')}
                        )
                    </h2>
                )}
                {!isModifyOrder && data && data.OrderSummary && data?.OrderSummary?.TotalQuantity != 0 && (
                    <h2 className="mb-5 mt-[15px] text-[19px] font-semibold">
                        {t('Order summary')} ({data?.OrderSummary?.TotalQuantity}{' '}
                        {data?.OrderSummary?.TotalQuantity < 2 ? t('item') : t('items')})
                    </h2>
                )}
                {/* PROMO CODE */}
                {isModifyOrder && (
                    <>
                        <Promocode isModifyOrder={isModifyOrder} />
                    </>
                )}
                {/* DONATION SELECTOR AND REMOVE */}
                {isDonationFlowEnabled && (
                    <>
                        {!isModifyOrder && (
                            <ErrorBoundary FallbackComponent={() => null}>
                                <DonationSelector
                                    data={data}
                                    isCartEmpty={getShowBagEmpty(data)}
                                    isOnlyDonationProductInCart={cartContainsOnlyDonation(data)}
                                />
                            </ErrorBoundary>
                        )}
                        {!isModifyOrder && donationProduct?.CartLineItem && (
                            <div className="mt-[30px] flex flex-wrap p-0 pb-2.5 text-base font-light">
                                <div className="flex-[1_1_73%] text-left">
                                    <span className="mr-2.5">{t('Jays Care Donation')}</span>
                                    <button
                                        className="flex-auto border-0 bg-none underline"
                                        onClick={() => {
                                            mutation.mutate();
                                        }}
                                    >
                                        {t('Remove')}
                                    </button>
                                </div>
                                <div className="flex-[1_1_27%] text-right">
                                    {donationProduct?.CartLineItem?.AppliedPrice}
                                </div>
                            </div>
                        )}
                    </>
                )}
                {showSummaryDetails && (
                    <>
                        {/* We hide summary details if only a donation is in cart */}
                        {/* SUBTOTAL */}
                        <div
                            className={`${
                                data?.CartLines && itemNumbers.includes(data?.CartLines[0]?.CartLineItem.ItemNo)
                                    ? ''
                                    : 'mt-[30px]'
                            } flex flex-wrap p-0 pb-2.5 text-base font-light`}
                        >
                            <div className="flex-[1_1_73%] text-left">
                                <span>{t('Subtotal')}</span>
                            </div>
                            <div className="flex-[1_1_27%] text-right">
                                {isModifyOrder && <span>{orderReviewData?.MerchandiseSubTotal}</span>}
                                {!isModifyOrder && <span>{data?.OrderSummary?.MerchandiseSubTotal}</span>}
                            </div>
                        </div>
                        {/* DISCOUNT ModifyOrder */}
                        {isModifyOrder && orderReviewData?.DiscountAmount > 0 && (
                            <div className="flex flex-wrap p-0 pb-2.5 text-base font-light">
                                <div className="flex-[1_1_73%] text-left">
                                    {orderReviewData?.AppliedPromocode == '' ||
                                    orderReviewData?.AppliedPromocode == null ||
                                    (orderReviewData?.AppliedPromocode != '' &&
                                        orderReviewData?.AppliedPromocode?.startsWith('SHIP')) ? (
                                        <span className="font-semibold">{t('DISCOUNT')}</span>
                                    ) : (
                                        <span className="font-semibold">{orderReviewData?.AppliedPromocode}</span>
                                    )}
                                </div>
                                <div className="flex-[1_1_27%] text-right">
                                    {orderReviewData.DiscountAmount > 0 && (
                                        <span className="font-semibold">-{orderReviewData?.Discount}</span>
                                    )}
                                </div>
                            </div>
                        )}
                        {/* DISCOUNT */}
                        {!isModifyOrder &&
                            data.PromoCode &&
                            data.PromoCode !== 'FREESHIP' &&
                            data.OrderSummary.Discount && (
                                <div className="flex flex-wrap p-0 pb-2.5 text-base font-light">
                                    <div className="flex-[1_1_73%] text-left">
                                        <span className="font-semibold">{data.PromoCode}</span>
                                        {data?.OrderSummary?.PromotionValue &&
                                            data?.OrderSummary?.Discount !== '$0.00' && (
                                                <span className="summary__label--discount">
                                                    {' '}
                                                    – {data.OrderSummary.PromotionValue}
                                                </span>
                                            )}
                                    </div>
                                    <div className="flex-[1_1_27%] text-right">
                                        <span className="font-semibold">
                                            {data?.OrderSummary?.Discount !== '$0.00' && <>–</>}
                                            {data?.OrderSummary?.Discount}
                                        </span>
                                    </div>
                                </div>
                            )}

                        {!data.PromoCode && data.OrderSummary.DiscountAmount > 0 && (
                            <div className="flex flex-wrap p-0 pb-2.5 text-base font-light">
                                <div className="flex-[1_1_73%] text-left">
                                    <span className="font-semibold">{t('DISCOUNT')}</span>
                                    {data?.OrderSummary?.PromotionValue && data?.OrderSummary?.Discount !== '$0.00' && (
                                        <span className="summary__label--discount">
                                            {' '}
                                            – {data.OrderSummary.PromotionValue}
                                        </span>
                                    )}
                                </div>
                                <div className="flex-[1_1_27%] text-right">
                                    <span className="font-semibold">
                                        {data.OrderSummary.DiscountAmount > 0 && <>–</>}
                                        {data.OrderSummary.Discount}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* SHIPPING */}
                        <div className="flex flex-wrap p-0 pb-2.5 text-base font-light">
                            <div className="flex-[1_1_73%] text-left">
                                <span>{t('Shipping')}</span>
                            </div>
                            <div className="flex-[1_1_27%] text-right">
                                {isModifyOrder ? (
                                    <span>{orderReviewData?.ShippingHandling}</span>
                                ) : (
                                    <span>
                                        {data?.OrderSummary?.ShippingHandling === '$0.00'
                                            ? t('Free')
                                            : data?.OrderSummary?.ShippingHandling}
                                    </span>
                                )}
                            </div>
                        </div>

                        {!isModifyOrder &&
                            data.PromoCode &&
                            data.PromoCode === 'FREESHIP' &&
                            data.OrderSummary.Discount && (
                                <div className="flex flex-wrap p-0 pb-2.5 text-base font-light">
                                    <div className="flex-[1_1_73%] text-left">
                                        <span className="font-semibold">{data.PromoCode}</span>
                                    </div>
                                    <div className="flex-[1_1_27%] text-right">
                                        <span className="font-semibold">
                                            {data.OrderSummary.Discount !== '$0.00' && <>–</>}
                                            {data.OrderSummary.Discount}
                                        </span>
                                    </div>
                                </div>
                            )}

                        {/* GIFTCARD Modify */}
                        {isModifyOrder && !!orderReviewData?.GiftCard && (
                            <div className="flex flex-wrap p-0 pb-2.5 text-base font-light">
                                <div className="flex-[1_1_73%] text-left">
                                    <span className="font-semibold">{t('Gift Card')}</span>
                                </div>
                                <div className="flex-[1_1_27%] text-right">
                                    {isModifyOrder && (
                                        <span className="font-semibold">-{orderReviewData?.GiftCard}</span>
                                    )}
                                </div>
                            </div>
                        )}
                        {/* GIFTCARD */}
                        {!isModifyOrder && !!data?.OrderSummary.GiftCard && (
                            <div className="flex flex-wrap p-0 pb-2.5 text-base font-light">
                                <div className="flex-[1_1_73%] text-left">
                                    <span className="font-semibold">{t('Gift Card')}</span>
                                </div>
                                <div className="flex-[1_1_27%] text-right">
                                    <span className="font-semibold">-{data?.OrderSummary?.GiftCard}</span>
                                </div>
                            </div>
                        )}
                        {/* TAXES */}
                        <div className="flex flex-wrap p-0 pb-2.5 text-base font-light">
                            <div className="flex-[1_1_73%] text-left">
                                {isModifyOrder && (
                                    <>
                                        <span>{t('Taxes')}</span>
                                        <span> ({orderReviewData?.ShipmentProvince})</span>
                                    </>
                                )}
                                {!isModifyOrder && (
                                    <>
                                        <span>{t('Est. Taxes')}</span>
                                        <select
                                            aria-label="Select Province for Tax Estimate"
                                            className="summary__province"
                                            value={taxProvince}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                estTaxMutation.mutate({
                                                    provinceId: e.target.value,
                                                    onSuccess: (data) => {
                                                        setEstTaxData(data);
                                                        setTaxProvince(val);
                                                    },
                                                });
                                            }}
                                        >
                                            {province.map((province, index) => (
                                                <option key={province.id} value={province.id}>
                                                    {province.id}
                                                </option>
                                            ))}
                                        </select>
                                    </>
                                )}
                            </div>

                            <div className="flex-[1_1_27%] text-right">
                                {isModifyOrder && <span>{orderReviewData?.TaxTotal}</span>}
                                {!isModifyOrder && (
                                    <>
                                        {taxProvince != defaultTax ? (
                                            <span>{estTaxData?.TaxTotal}</span>
                                        ) : (
                                            <span>{data?.OrderSummary?.TaxTotal}</span>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </>
                )}
                {/* TOTAL Modify */}
                {isModifyOrder && (
                    <div className="flex flex-wrap p-0 pb-2.5 text-base font-light">
                        <div className="flex-[1_1_73%] text-left">
                            {orderReviewData?.PreviousOrderTotal != orderReviewData?.GrandTotal ? (
                                <span className="my-[15px] block text-[19px] font-semibold">{t('NEW TOTAL')}</span>
                            ) : (
                                <span className="my-[15px] block text-[19px] font-semibold">{t('Total')}</span>
                            )}
                        </div>
                        <div className="flex-[1_1_27%] text-right">
                            <span className="my-[15px] block text-[19px] font-semibold">
                                {orderReviewData?.GrandTotal}
                            </span>
                        </div>
                    </div>
                )}
                {/* TOTAL */}
                {!isModifyOrder && data?.OrderSummary?.TotalQuantity > 0 && (
                    <div className="flex flex-wrap p-0 pb-2.5 text-base font-light">
                        <div className="flex-[1_1_73%] text-left">
                            <span className="my-[15px] block text-[19px] font-semibold">{t('Total')}</span>
                        </div>
                        <div className="flex-[1_1_27%] text-right">
                            {taxProvince != defaultTax ? (
                                <span className="my-[15px] block text-[19px] font-semibold">
                                    {estTaxData?.GrandTotal}
                                </span>
                            ) : (
                                <span className="my-[15px] block text-[19px] font-semibold">
                                    {data?.OrderSummary?.GrandTotal}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* ORIGINAL TOTAL Modify */}
                {isModifyOrder && orderReviewData?.PreviousOrderTotal != orderReviewData?.GrandTotal && (
                    <div className="mx-0 mb-[30px] mt-2.5">
                        <div className="flex flex-wrap p-0 pb-2.5 text-base font-light">
                            <div className="flex-[1_1_73%] text-left">{t('Original total')}</div>
                            <div className="flex-[1_1_27%] text-right">{orderReviewData?.PreviousOrderTotal}</div>
                        </div>
                        <div className="flex flex-wrap p-0 pb-2.5 text-base font-light">
                            <div className="flex-[1_1_73%] text-left">{t('Change to original total')}</div>
                            <div className="flex-[1_1_27%] text-right">{orderReviewData?.OrderTotalDifference}</div>
                        </div>
                    </div>
                )}
                {/* EASY PAY */}
                {isModifyOrder ? (
                    <>
                        <EasyPayModify isModifyOrder={isModifyOrder} />
                    </>
                ) : (
                    <>
                        <Easypay
                            setIsPaypalDisabled={setIsPaypalDisabled}
                            showEasyPayNotice={showEasyPayNotice}
                            setShowEasyPayNotice={setShowEasyPayNotice}
                            setEasyWarningBlockCheckout={setEasyWarningBlockCheckout}
                        />
                    </>
                )}

                {isModifyOrder && (
                    <>
                        {/* CHECKOUT BUTTON */}
                        <button
                            className="mx-auto my-0 mt-[30px] block w-full max-w-[750px] cursor-pointer select-none whitespace-nowrap border border-solid border-black bg-black px-3 py-3.5 text-center align-middle text-sm font-semibold text-white disabled:mt-[15px] disabled:cursor-default disabled:border-neutral-200 disabled:bg-neutral-200 disabled:text-neutral-600"
                            type="button"
                            aria-label={t('Proceed to checkout')}
                            disabled={isCheckoutDisabled || isModifyCheckoutDisabled}
                            onClick={() => {
                                onCheckout();
                            }}
                        >
                            <span>
                                {t('Proceed to checkout')} (
                                {order?.data?.OrderSummary.Quantity + data?.OrderSummary?.TotalQuantity}{' '}
                                {order?.data?.OrderSummary.Quantity + data?.OrderSummary?.TotalQuantity < 2
                                    ? t('item')
                                    : t('items')}
                                )
                            </span>
                        </button>
                        {/* CANCEL ORDER MODIFICATION MODE */}
                        <button
                            className="mt-5 block w-full cursor-pointer select-none whitespace-nowrap border border-solid border-black bg-white px-3 py-3.5 text-center align-middle text-sm font-semibold text-black"
                            type="button"
                            aria-label={t('Cancel modification')}
                            onClick={() => {
                                setShowCancelDialog(true);
                            }}
                        >
                            <span>{t('Cancel modification')}</span>
                        </button>
                    </>
                )}
                {/* CHECKOUT BUTTON */}
                {!isModifyOrder && data?.OrderSummary?.TotalQuantity > 0 && (
                    <button
                        className="mx-auto my-0 block w-full !max-w-screen-md3 cursor-pointer select-none whitespace-nowrap border border-solid border-black bg-black px-3 py-3.5 text-center align-middle text-sm font-semibold text-white disabled:mt-[15px] disabled:cursor-default disabled:border-neutral-200 disabled:bg-neutral-200 disabled:text-neutral-600"
                        type="button"
                        disabled={isCheckoutDisabled}
                        aria-label={t('Proceed to checkout')}
                        onClick={() => {
                            onCheckout();
                        }}
                    >
                        <span>
                            {t('Proceed to checkout')} ({data?.OrderSummary.TotalQuantity}{' '}
                            {data?.OrderSummary.TotalQuantity < 2 ? t('item') : t('items')})
                        </span>
                    </button>
                )}
                {/* PAYPAL BUTTON */}
                <div
                    id="tscPaypal"
                    ref={paypalRef}
                    className={hidePaypalButton ? 'hidden' : 'mx-auto; mb-0 mt-[25px] !max-w-screen-md3 text-center'}
                ></div>
                {/* SUMMARY GIFTCARD MSG */}
                {!isModifyOrder && data?.OrderSummary?.TotalQuantity > 0 && (
                    <div className="mt-5 text-center text-xsm font-semibold">
                        <div>{t('Have a gift card or promo code?')}</div>
                        <div>{t('Enter it during checkout.')}</div>
                    </div>
                )}
                {showCancelDialog && (
                    <>
                        <Dialog
                            title={t('Are you sure?')}
                            containerCss="removeDialog"
                            height="auto"
                            ignoreOverlayClick={true}
                            style={{ cursor: 'auto' }}
                            onCloseClick={() => {
                                setShowCancelDialog(!showCancelDialog);
                            }}
                        >
                            <p>
                                {t(
                                    'You have unsaved changes to your Order Modification. If you cancel now your order will not be modified. The original order that you placed will be processed as is.',
                                )}
                            </p>
                            <div className="flex flex-col gap-y-[0.4375rem] p-[5px] md:flex-row md:justify-between md:gap-x-[25px]">
                                <button
                                    type="button"
                                    aria-label={t('Cancel modification')}
                                    className="mb-0 mt-5 inline-block w-full cursor-pointer select-none items-center whitespace-nowrap border border-solid border-black bg-black px-3 py-3.5 text-center align-middle text-sm font-medium text-white shadow-[0_0_0_3px_#0000000d] hover:font-semibold md:order-2"
                                    onClick={() => {
                                        cookie.deleteModifyOrderInfo();
                                        setupAnalyticsCancelModification();
                                        window.location.href = '/pages/myaccount/orderstatus';
                                        setShowCancelDialog(!showCancelDialog);
                                    }}
                                >
                                    {t('Cancel modification')}
                                </button>
                                <button
                                    type="button"
                                    aria-label={t('Stay on page')}
                                    className="mb-0 mt-5 inline-block w-full cursor-pointer select-none items-center whitespace-nowrap border border-solid border-black bg-white px-3 py-3.5 text-center align-middle text-sm font-medium shadow-[0_0_0_3px_#0000000d] hover:font-semibold"
                                    onClick={() => {
                                        setShowCancelDialog(!showCancelDialog);
                                    }}
                                >
                                    {t('Stay on page')}
                                </button>
                            </div>
                        </Dialog>
                    </>
                )}
            </div>
        </div>
    );
};

export default OrderSummary;
